/**
 * Новый счёт — один лист для настройки и для «+» на «Счетах» (22.10.2026).
 *
 * Прежде в настройке вид задавала метка на шаге и в листе его было не сменить,
 * не было ни значка, ни цвета, а счёт с нулём не сохранялся. В приложении вид
 * выбирали метками, значок и цвет — двумя полями, а у кредитки не было
 * «Внести для беспроцентного»: подставлялся весь долг. Накопительный,
 * добавленный в приложении, считался деньгами на траты.
 *
 * Теперь лист один и устроен как лист кредита: вид — переключатель сверху,
 * сумма крупно, остальное строками. Кредитка собирает все поля из обоих мест;
 * день выписки и минимальный платёж — под «Ещё условия» (прежде их можно было
 * задать только при правке карты).
 */
import { useState } from 'react';
import { ZERO, accountIcon, type Account, type Loan, type Money } from '@skoka/core';
import { accountColor } from './account-glyph.js';
import { bankOf } from './banks.js';
import type { CategoryColor } from './category-icon.js';
import { SheetWrap } from './sheet.js';
import {
  BigAmount, CardDays, CardHead, CardInput, CardLook, CardMoney, CardPick, CardToggle, MoreButton, Segs,
} from './card-fields.js';
import { addAccount, addCreditCard, parseAmountInput, parseRateInput, upsertCreditTerms } from './store.js';

export type AccKind = 'debit' | 'cash' | 'savings' | 'credit_card';

/** Счёт в черновике. Поля кредитки у остальных видов пустые. */
export interface AccountDraft {
  name: string;
  /** Остаток; у кредитки — текущий долг, положительным числом. */
  balance: string;
  kind: AccKind;
  limit: string;
  /** Сколько внести для беспроцентного периода — сумма из выписки. */
  needGrace: string;
  payDay: string;
  /** Беспроцентный период, дней; «0» — его нет. */
  grace: string;
  /** Ставка вне беспроцентного периода: по ней считается цена просрочки. */
  rate: string;
  /** Уже внесено на карту после выписки. */
  reserved?: string;
  /** Какого числа выписка; пусто — не знаю. */
  statementDay?: string;
  /** Минимальный платёж, % от долга по выписке, и «но не меньше». */
  minPct?: string;
  minFloor?: string;
  /** Значок и цвет; пусто — по виду счёта. */
  icon?: string;
  color?: string;
  /** Деньги для трат. Пусто — по виду: накопительный не для трат. */
  liquid?: boolean;
  /** Найден на скриншоте банка — что в нём проверить; проверен — пусто. */
  shotWarn?: string;
}

export const newAccount = (name: string, kind: AccKind): AccountDraft => ({
  name, balance: '', kind, limit: '', needGrace: '', payDay: '10', grace: '55', rate: '',
});

/** Имя по виду — пока человек не назвал своё. */
export const KIND_NAME: Record<AccKind, string> = {
  debit: 'Карта', cash: 'Наличные', savings: 'Накопительный', credit_card: 'Кредитка',
};
const KINDS = [['debit', 'Карта'], ['cash', 'Наличные'], ['savings', 'Накопит.'], ['credit_card', 'Кредитка']] as const;

const GRACE_OPTIONS = [
  { id: '55', title: '55 дней', sub: 'чаще всего' },
  { id: '100', title: '100 дней' },
  { id: '120', title: '120 дней' },
  { id: 'other', title: 'Другой', sub: 'впишешь число дней' },
  { id: '0', title: 'Нет', sub: 'проценты с первого дня' },
];

/**
 * Беспроцентный период: 55, 100, 120 дней, свой или нет. Свой — строкой
 * «Сколько дней» ниже. Один выбор для новой кредитки и для правки карты.
 */
export function GraceRows({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const known = ['55', '100', '120', '0'].includes(value.trim());
  const [other, setOther] = useState(!known);
  return (
    <>
      <CardPick label="Беспроцентный период" value={other ? 'other' : value.trim()} options={GRACE_OPTIONS}
        onChange={(id) => {
          if (id === 'other') { setOther(true); if (known) onChange(''); return; }
          setOther(false); onChange(id);
        }} />
      {other && (
        <CardInput label="Сколько дней" value={value} onChange={onChange} inputMode="numeric" placeholder="180" width={60} />
      )}
    </>
  );
}

const dayNum = (sv: string): number => Math.min(31, Math.max(1, Math.trunc(Number(sv) || 1)));
/** Деньги для трат: названные человеком, иначе по виду. */
export const liquidOf = (a: AccountDraft): boolean => a.liquid ?? (a.kind !== 'savings' && a.kind !== 'credit_card');

/**
 * Завести счёт из черновика: тем же путём в настройке и в приложении.
 * Кредитка — и счёт, и условия, и долг в «Долгах».
 */
export async function createAccount(a: AccountDraft): Promise<{ account: Account; loan?: Loan }> {
  const name = a.name.trim() || KIND_NAME[a.kind];
  const money = (s: string | undefined): Money => parseAmountInput(s ?? '');
  // Значок и цвет по умолчанию не записываем: они следуют за видом счёта.
  const look = {
    ...(a.icon && a.icon !== accountIcon({ kind: a.kind }) ? { icon: a.icon } : {}),
    ...(a.color && !bankOf(a.icon) && a.color !== accountColor({ kind: a.kind }) ? { color: a.color } : {}),
  };
  if (a.kind === 'credit_card') {
    const debt = money(a.balance);
    const { account, loan } = await addCreditCard({
      name, debt, ...look,
      limit: money(a.limit),
      // Не знаем сумму из выписки — берём весь долг: осторожная оценка
      // не пообещает лишних денег.
      statementDebt: money(a.needGrace) > ZERO ? money(a.needGrace) : debt,
      reserved: money(a.reserved),
      paymentDay: dayNum(a.payDay),
      graceDays: Math.max(0, Math.trunc(Number(a.grace) || 0)),
      ratePct: parseRateInput(a.rate) ?? 0,
    });
    // День выписки и минимальный платёж — только названные: подставленное
    // стало бы условием, которого банк не ставил.
    const pct = (a.minPct ?? '').trim() === '' ? null : Number((a.minPct ?? '').replace(',', '.'));
    const floor = money(a.minFloor);
    const extra = {
      ...((a.statementDay ?? '').trim() !== '' ? { statementDay: dayNum(a.statementDay!) } : {}),
      ...(pct !== null && Number.isFinite(pct) && pct > 0 ? { minPaymentPct: pct / 100 } : {}),
      ...(floor > ZERO ? { minPaymentFloor: floor } : {}),
    };
    if (Object.keys(extra).length > 0) await upsertCreditTerms(account.id, extra);
    return { account, loan };
  }
  const account = await addAccount({ name, kind: a.kind, balance: money(a.balance), isLiquid: liquidOf(a), ...look });
  return { account };
}

export function AccountSheet({ init, isNew, defaultName, onSave, onDelete, onClose, title, lead }: {
  init: AccountDraft;
  isNew: boolean;
  /** Заголовок вместо «Новый счёт» / названия: «Проверь счёт» со скриншота. */
  title?: string;
  /** Плашка сверху листа: откуда заполнено и что проверить. */
  lead?: React.ReactNode;
  /** Имя по виду: у второй кредитки — «Кредитка 2». */
  defaultName?: (kind: AccKind) => string;
  onSave: (v: AccountDraft) => void | Promise<void>;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const [v, setV] = useState<AccountDraft>(init);
  const [busy, setBusy] = useState(false);
  const on = <K extends keyof AccountDraft>(k: K, x: AccountDraft[K]) => setV((p) => ({ ...p, [k]: x }));
  const nameOf = (k: AccKind) => defaultName?.(k) ?? KIND_NAME[k];
  const isCard = v.kind === 'credit_card';
  const [more, setMore] = useState(Boolean(v.statementDay || v.minPct || v.minFloor));
  const icon = v.icon || accountIcon({ kind: v.kind });
  const color = (v.color || accountColor({ kind: v.kind })) as CategoryColor;
  const liquid = liquidOf(v);

  // Вид сменили — имя по виду меняется вместе с ним, своё остаётся.
  const setKind = (k: AccKind) => setV((p) => {
    const auto = p.name.trim() === '' || (Object.keys(KIND_NAME) as AccKind[]).some((x) => p.name === nameOf(x) || p.name === KIND_NAME[x]);
    return { ...p, kind: k, ...(auto ? { name: nameOf(k) } : {}), icon: '', color: '' };
  });

  // Кредитка без лимита не участвует в резерве, и вся логика беспроцентного
  // периода не работает. Ноль на обычном счёте — можно: наличные появятся позже.
  const ready = !busy && v.name.trim() !== '' && (!isCard || parseAmountInput(v.limit) > ZERO);

  return (
    <SheetWrap title={title ?? (isNew ? 'Новый счёт' : v.name || 'Счёт')} onClose={onClose} className="rcpt">
      {lead}
      <div className="rcform">
        <Segs label="Вид счёта" options={KINDS} value={v.kind} onChange={setKind} />
        <BigAmount label={isCard ? 'Текущий долг' : 'Сколько сейчас'} value={v.balance} onChange={(x) => on('balance', x)} />

        <div className="rccard rcsel">
          <CardInput label="Название" value={v.name} onChange={(x) => on('name', x)}
            placeholder={isCard ? 'Кредитка Альфа' : nameOf(v.kind)} width={170} />
          {isCard && (
            <>
              <CardMoney label="Кредитный лимит" value={v.limit} onChange={(x) => on('limit', x)} placeholder="100 000"
                tip="Лимит деньгами не считается: это не твои деньги." />
              <CardDays label="До какого числа платёж" value={[dayNum(v.payDay)]} sub="каждый месяц"
                onChange={(ds) => on('payDay', String(ds[0] ?? 10))} />
              <GraceRows value={v.grace} onChange={(x) => on('grace', x)} />
              <CardInput label="Ставка" value={v.rate} onChange={(x) => on('rate', x)} unit="% годовых"
                inputMode="decimal" placeholder="39" width={44}
                tip="Сколько банк возьмёт, если беспроцентный период сорвётся. По ней «Долги» покажут, во что обойдётся карта." />
            </>
          )}
          <CardLook icon={icon} color={color} onChange={(i, c) => setV((p) => ({ ...p, icon: i, color: c }))} />
          {!isCard && (
            <CardToggle label="Тратим отсюда" checked={liquid} onChange={(x) => on('liquid', x)}
              sub={liquid ? 'входит в «Можно тратить»' : 'не входит в «Можно тратить»: отложить — не потратить'} />
          )}
        </div>

        {isCard && (
          <>
            <CardHead>По выписке</CardHead>
            <div className="rccard rcsel">
              <CardMoney label="Внести для беспроцентного" value={v.needGrace} onChange={(x) => on('needGrace', x)}
                placeholder="сумма"
                tip="Сумма из выписки. Не знаешь — оставь пустым, поставлю весь долг: осторожная оценка не пообещает лишних денег." />
              <CardMoney label="Уже внесено после выписки" value={v.reserved ?? ''} onChange={(x) => on('reserved', x)}
                tip="Только то, что уже ушло со счёта на карту. Деньги, которые лежат под платёж на другом счёте, сюда не пиши." />
            </div>
            <MoreButton label="Ещё условия" open={more} onToggle={() => setMore(!more)} />
            {more && (
              <div className="rccard rcsel">
                <CardDays label="Какого числа выписка" empty="если знаешь"
                  value={(v.statementDay ?? '').trim() ? [dayNum(v.statementDay!)] : []}
                  onChange={(ds) => on('statementDay', ds[0] ? String(ds[0]) : '')}
                  tip="Это другое число, не день платежа: у карты с платежом до 9-го выписка может приходить 16-го." />
                <CardInput label="Минимальный платёж" value={v.minPct ?? ''} onChange={(x) => on('minPct', x)}
                  unit="% от долга" inputMode="decimal" placeholder="5" width={36}
                  tip="Смотри в тарифе карты или в выписке. Пока не заполнено, минимальный платёж в план не входит." />
                <CardMoney label="но не меньше" value={v.minFloor ?? ''} onChange={(x) => on('minFloor', x)} placeholder="если есть" />
              </div>
            )}
          </>
        )}

        <button className="btn pri" disabled={!ready}
          onClick={async () => {
            setBusy(true);
            try { await onSave({ ...v, name: v.name.trim(), liquid }); } finally { setBusy(false); }
          }}>{isNew ? 'Добавить' : 'Сохранить'}</button>
        {onDelete && !isNew && (
          <button className="textact danger" disabled={busy}
            onClick={() => { if (confirm('Удалить счёт?')) onDelete(); }}>Удалить</button>
        )}
      </div>
    </SheetWrap>
  );
}
